import { max } from "moment";
import { isSet, optionsToKeyValuePairs } from "../../../../../../utils/commonUtils";
import { OnTabKeyPress } from "../../../../../../utils/cssUtils";
import { fetchProviderList } from "./NonDOTExamBPVisionAudio1";
import { computationRules1 } from "./DrugScreenCollection";

// Tab IDs - matches fitforduty.html (History, Exam, Images, Certification)
const TAB_IDS = { HISTORY: "history", EXAM: "exam", CERTIFICATION: "certification", IMAGES: "images" };

// Past Medical Illness options - matches fitforduty.html
const PAST_MEDICAL_ILLNESS_OPTIONS = [
  { label: "Anemia", name: "anemia" },
  { label: "Anxiety", name: "anxiety" },
  { label: "Asthma", name: "asthma" },
  { label: "Cancer", name: "cancer" },
  { label: "Cataracts", name: "cataracts" },
  { label: "COPD or Lung Disease", name: "copd_or_lung_disease" },
  { label: "Diabetes Type I", name: "diabetes_type_i" },
  { label: "Diabetes Type II", name: "diabetes_type_ii" },
  { label: "Epilepsy", name: "epilepsy" },
  { label: "GERD/GI Disorder", name: "gerd_gi_disorder" },
  { label: "Glaucoma", name: "glaucoma" },
  { label: "Headaches", name: "headaches" },
  { label: "Heart Disease", name: "heart_disease" },
  { label: "Hepatitis", name: "hepatitis" },
  { label: "High Blood Pressure", name: "high_blood_pressure" },
  { label: "Hypercholesterolemia", name: "hypercholesterolemia" },
  { label: "Hypothyroid", name: "hypothyroid" },
  { label: "Hyperthyroid", name: "hyperthyroid" },
  { label: "Injury: non-work", name: "injury_non_work" },
  { label: "Injury: work", name: "injury_work" },
  { label: "Kidney Disease", name: "kidney_disease" },
  { label: "Liver Disease", name: "liver_disease" },
  { label: "Motor Vehicle Accident", name: "motor_vehicle_accident" },
  { label: "Musculoskeletal disorder", name: "musculoskeletal_disorder" },
  { label: "Neck or lower back pain", name: "neck_or_lower_back_pain" },
  { label: "Neuropathy", name: "neuropathy" },
  { label: "Sickle Cell Trait", name: "sickle_cell_trait_or_disease" },
  { label: "Stroke/CVA/TIA", name: "stroke_cva_tia" },
  { label: "Needles", name: "needles" },
  { label: "Tuberculosis", name: "tuberculosis" },
  { label: "Rubella", name: "rubella" },
  { label: "Varicella (Chicken Pox)", name: "varicella_chicken_pox" },
  { label: "Mumps", name: "mumps" },
  { label: "Other Past Medical Conditions", name: "other" },
];

const PAST_SURGICAL_HISTORY_OPTIONS = [
  { label: "Appendectomy", name: "appendectomy" },
  { label: "Breast reduction", name: "breast_reduction" },
  { label: "C-Section", name: "c_section" },
  { label: "Cataract removal", name: "cataract_removal" },
  { label: "Cholecystectomy", name: "cholecystectomy" },
  { label: "Hemorrhoidectomy", name: "hemorrhoidectomy" },
  { label: "Hysterectomy", name: "hysterectomy" },
  { label: "Laminectomy", name: "laminectomy" },
  { label: "Liposuction", name: "liposuction" },
  { label: "Lumpectomy", name: "lumpectomy" },
  { label: "Mastectomy", name: "mastectomy" },
  { label: "Ovarian cystectomy", name: "ovarian_cystectomy" },
  { label: "Salpingectomy", name: "salpingectomy" },
  { label: "Thyroidectomy", name: "thyroidectomy" },
  { label: "Tonsillectomy", name: "tonsillectomy" },
  { label: "Tubal Ligation", name: "tubal_ligation" },
];

const ALLERGIES_OPTIONS = [
  { label: "Seasonal", name: "seasonal" },
  { label: "Penicillin", name: "penicillin" },
  { label: "Environmental", name: "environmental" },
  { label: "Perfume", name: "perfume" },
  { label: "Sulfa", name: "sulfa" },
  { label: "Vinyl", name: "vinyl" },
  { label: "Nitrate", name: "nitrate" },
  { label: "Latex", name: "latex" },
  { label: "Animal/Pet Dander", name: "animal_pet_dander" },
  { label: "Other/Drug", name: "other_drug" },
];

const PHYSICAL_EXAM_SECTIONS = [
  { label: "General", name: "general", notesName: "general_notes" },
  { label: "Respiratory", name: "respiratory", notesName: "respiratory_notes" },
  { label: "Cardiovascular", name: "cardiovascular", notesName: "cardiovascular_notes" },
  { label: "Gastrointestinal", name: "gastrointestinal", notesName: "gastrointestinal_notes" },
  { label: "Urinary", name: "urinary", notesName: "urinary_notes" },
  { label: "Musculoskeletal", name: "musculoskeletal", notesName: "musculoskeletal_notes" },
  { label: "Neurological", name: "neurological", notesName: "neurological_notes" },
  { label: "Skin", name: "skin", notesName: "skin_notes" },
  { label: "Endocrine", name: "endocrine", notesName: "endocrine_notes" },
  { label: "Hematology", name: "hematology", notesName: "hematology_notes" },
  { label: "Allergy / Immunology", name: "allergy_immunology", notesName: "allergy_immunology_notes" },
  { label: "Psychiatric", name: "psychiatric", notesName: "psychiatric_notes" },
];

export const ExamFitForDuty = (props, type = "Others") => {
  const stackedFieldStyle = {
    layout: "stacked",
    showContainerBorder: false,
    labelWrap: false,
    isLabel: true,
  };

  const getClearRule = (fieldName) => ({
    rule: [{ field: fieldName, value: 1, operator: "===" }],
    action: "CLEAR",
  });

  const getShowRule = (fieldName, value) => ({
    rule: [{ field: fieldName, value, operator: "includes" }],
    action: "SHOW",
  });

  // Shared section styling
  const sectionHeaderStyle = { backgroundColor: "#F0F3FD" };
  const sectionHeaderClass = "border-0 p-2";
  const sectionBodyClass = "p-3 bg-white";
  const sectionLabelStyle = "font-weight-bold";

  // History tab 
  const historyTabData = [
    {
      label: "Past Medical Illness:",
      labelStyle: sectionLabelStyle,
      headerClassName: sectionHeaderClass,
      headerStyle: sectionHeaderStyle,
      cardBodyClassName: sectionBodyClass,
      headerSwitch: { name: "past_medical_illness_none", label: "None" },
      fields: [
        { label: "Past Medical Illness:", col: "col-12", type: "CheckBoxGroup", name: "past_medical_illness", id: "past_medical_illness", combineToKey: "past_medical_illness", options: PAST_MEDICAL_ILLNESS_OPTIONS, showContainerBorder: false, columns: 4, isLabel: false, rules: [getClearRule("past_medical_illness_none")] },
        { label: "Please specify", type: "TextInput", name: "past_medical_illness_other_specify", col: "col-12 border-0 pl-4", id: "past_medical_illness_other_specify", className: "form-control form-control-sm", placeholder: "Please specify", isLabel: false, rules: [{ rule: [{ field: "past_medical_illness", value: "other", operator: "includes" }], action: "SHOW" }] },
      ],
    },
    {
      label: "Past Surgical History",
      labelStyle: sectionLabelStyle,
      headerClassName: sectionHeaderClass,
      headerStyle: sectionHeaderStyle,
      cardBodyClassName: sectionBodyClass,
      headerSwitch: { name: "past_surgical_history_none", label: "None" },
      fields: [
        { label: "Past Surgical History:", col: "col-12", type: "CheckBoxGroup", name: "past_surgical_history", id: "past_surgical_history", combineToKey: "past_surgical_history", options: PAST_SURGICAL_HISTORY_OPTIONS, showContainerBorder: false, columns: 3, isLabel: false, rules: [getClearRule("past_surgical_history_none")] },
        { label: "Date of Last Surgery", type: "Date", name: "date_of_last_surgery", col: "col-md-4 col-12 pl-4", id: "date_of_last_surgery", className: "form-control form-control-sm", placeholder: "DD/MM/YYYY", layout: "stacked", showContainerBorder: false },
        { label: "Others Surgical Notes", type: "TextInput", name: "other_surgical_notes", col: "col-md-8 col-12", id: "other_surgical_notes", placeholder: "Content", className: "form-control form-control-sm", layout: "stacked", showContainerBorder: false },
      ],
    },
    {
      label: "Medications:",
      labelStyle: sectionLabelStyle,
      headerClassName: sectionHeaderClass,
      headerStyle: sectionHeaderStyle,
      cardBodyClassName: sectionBodyClass,
      headerSwitch: { name: "medications_none", label: "None" },
      fields: [
        { label: "If Use Reported, list all medications:", col: "col-12", type: "Label", labelStyle: { color: "#2d3748" }, border: false },
        ...["1", "2", "3", "4", "5", "6"].map((i) => ({
          label: `Medication Name ${i}`,
          type: "TextInput",
          col: "col-md-6 col-12",
          name: `medication_name_${i}`,
          id: `medication_name_${i}`,
          placeholder: "Content",
          className: "form-control form-control-sm",
          rules: [getClearRule("medications_none")],
          layout: "stacked",
          showContainerBorder: false,
        })),
      ],
    },
    {
      label: "Allergies:",
      labelStyle: sectionLabelStyle,
      headerClassName: sectionHeaderClass,
      headerStyle: sectionHeaderStyle,
      cardBodyClassName: sectionBodyClass,
      headerSwitch: { name: "allergies_none", label: "None" },
      fields: [
        { label: "Allergies:", col: "col-12", type: "CheckBoxGroup", name: "allergies", id: "allergies", combineToKey: "allergies", options: ALLERGIES_OPTIONS, showContainerBorder: false, columns: 3, isLabel: false, rules: [getClearRule("allergies_none")] },
        { label: "Other Known Allergic Reactions:", type: "TextInput", name: "allergies_other_reactions", col: "col-12 pl-4", id: "allergies_other_reactions", placeholder: "Content", className: "form-control form-control-sm w-100", layout: "stacked", showContainerBorder: false, rules: [getClearRule("allergies_none")] },        
      ],
    },
    {
      label: "Social History:",
      labelStyle: sectionLabelStyle,
      headerClassName: sectionHeaderClass,
      headerStyle: sectionHeaderStyle,
      cardBodyClassName: sectionBodyClass,
      fields: [
        { label: "Tobacco Use:", col: "col-12", type: "RadioGroup", name: "tobacco_use", id: "tobacco_use", options: ["Does not smoke", "Social Smoker", "Smoke several cigarettes per day", "Smokes >1 pack/day"], layout: "stacked", showContainerBorder: false, labelStyle: { fontWeight: "bold" }, appendClassLabel: "font-weight-normal" },
        { label: "Length of Tobacco Use:", col: "col-12", type: "RadioGroup", name: "length_of_tobacco_use", id: "length_of_tobacco_use", options: ["1-5 years", "5-10 years", ">10 years"], layout: "stacked", showContainerBorder: false, labelStyle: { fontWeight: "bold" }, appendClassLabel: "font-weight-normal" },
        { label: "Alcohol Use:", col: "col-12", type: "RadioGroup", name: "alcohol_use", id: "alcohol_use", options: ["Does not consume alcohol", "Consumes alcohol on a daily basis", "Consumes alcohol socially", "Consumes alcohol occasionally"], layout: "stacked", showContainerBorder: false, labelStyle: { fontWeight: "bold" }, appendClassLabel: "font-weight-normal" },
      ],
    },
    {
      label: "Gynecological Information",
      labelStyle: sectionLabelStyle,
      headerClassName: sectionHeaderClass,
      headerStyle: sectionHeaderStyle,
      cardBodyClassName: sectionBodyClass,
      headerSwitch: { name: "menstrual_comments_none", label: "None" },
      fields: [
        { label: "Last Menstrual Period", type: "Date", labelWrap: false, name: "last_menstrual_period", col: "col-md-4 col-12", id: "last_menstrual_period", className: "form-control form-control-sm", placeholder: "MM/DD/YYYY", layout: "stacked", showContainerBorder: false },
        { label: "Last Menstrual Period Comments (select which apply):", col: "col-12", type: "Label", labelStyle: { color: "#2d3748" }, border: false },
        { label: "Last Menstrual Period Comments (select which apply):", col: "col-12", type: "CheckBoxGroup", name: "menstrual_comments", id: "menstrual_comments", options: [{ label: "Irregular menstrual cycle", name: "irregular_menstrual_cycle" }, { label: "Pregnancy", name: "pregnancy" }, { label: "Menopause", name: "menopause" }, { label: "Perimenopause", name: "perimenopause" }, { label: "Oophorectomy", name: "oophorectomy" }, { label: "Hysterectomy", name: "hysterectomy" }, { label: "Depo-Provera", name: "depo_provera" }, { label: "Breastfeeding", name: "breastfeeding" }, { label: "Implanon", name: "implanon" }, { label: "Unknown", name: "unknown" }], combineToKey: "menstrual_comments", showContainerBorder: false, columns: 3, isLabel: false, rules: [getClearRule("menstrual_comments_none")] },
      ],
    },
    {
      label:'Patient Signature & Comments',
       labelStyle: "fw-semibold",
      col:'col-12',
      fields:[
           {
             label: 'Comments',
             placeholder: 'Content',
             type: 'TextArea',
             name: 'patient_comments',
             id: 'patient_comments',
             col: 'col-md-6 col-12 border-0',
             layout: 'stacked',
             labelStyle: { paddingLeft: '0.75rem' },
             style: { minHeight: '150px', resize: 'vertical' },
             rows: 5,
           },
           {
             label: "Signature",
             type: 'NewSignature',
             isLabel: false,
             col: 'col-md-6 col-12  border-0',
             name: 'driver_signature',
             id: 'driver_signature',
             required: true,
           },
      ]
  },
  ];

  // Exam tab - Vital Signs & Measurements + Physical Exam table
  const examTabData = [
    {
      label: "Vital Signs & Measurements",
      labelStyle: sectionLabelStyle,
      headerClassName: sectionHeaderClass,
      headerStyle: sectionHeaderStyle,
      cardBodyClassName: sectionBodyClass,
      fields: [
        {
          label: "Blood Pressure Systolic (1st Reading)",
          isLabel: false,
          col: "col-6 border-0 ",
          type: "NewNumber",
          placeholder: "Blood pressure",
          rangeMax: 139,
          rangeMin: 90,
          name: "sitting_systolic",
          id: "sitting_systolic",
          onKeyPress: (event) => OnTabKeyPress(event, "sitting_diastolic", ""),
          Max: 3,
        },
        {
          label: "Blood Pressure Systolic (2nd Reading)",
          isLabel: false,
          col: "col-6 border-0 ",
          type: "NewNumber",
          placeholder: "Blood pressure",
          rangeMax: 139,
          rangeMin: 90,
          name: "second_reading_systolic",
          id: "second_reading_systolic",
          Max: 3,
        },
        {
          label: "Blood Pressure Diastolic (1st Reading)",
          isLabel: false,
          col: "col-6 border-0 ",
          type: "NewNumber",
          placeholder: "Blood pressure",
          rangeMin: 60,
          rangeMax: 89,
          name: "sitting_diastolic",
          id: "sitting_diastolic",
          onKeyPress: (event) => OnTabKeyPress(event, "pulse"),
          Max: 3,
        },
        {
          label: "Blood Pressure Diastolic (2nd Reading)",
          isLabel: false,
          col: "col-6 border-0 ",
          type: "NewNumber",
          placeholder: "Blood pressure",
          rangeMin: 60,
          rangeMax: 89,
          name: "second_reading_diastolic",
          id: "second_reading_diastolic",
          Max: 3,
        },
        {
          label: "Pulse Rate",
          isLabel: false,
          col: "col-6 border-0 ",
          type: "NewNumber",
          placeholder: "Pulse Rate",
          name: "pulse",
          id: "pulse",
          rangeMin: 60,
          rangeMax: 100,
          Max: 3,
        },
        {
          label: "Pulse Rhythm Regular",
          type: "RadioGroup",
          col: "col-6 border-0 ",
          name: "pulse_rhythm_regular",
          id: "pulse_rhythm_regular",
          options: [
            { label: "Yes", value: "Yes" },
            { label: "No", value: "No" },
          ],
        },
        {
          label: "Height (Feet)",
          placeholder: "Feet",
          isLabel: false,
          col: "col-6 border-0 ",
          type: "NewNumber",
          name: "height_feet",
          id: "height_feet",
          Max: 1,
        },
        {
          label: "Inches",
          isLabel: false,
          placeholder: "Inches",
          col: "col-6 border-0 ",
          type: "NewNumber",
          name: "height_inches",
          id: "height_inches",
          decimalAllowed: true,
          Max: 5,
        },
        {
          label: "Weight (Pounds)",
          isLabel: false,
          col: "col-6 border-0 ",
          type: "NewNumber",
          placeholder: "Pounds",
          name: "weight",
          id: "weight",
          Max: 6,
          decimalAllowed: true,
        },
        {
          label: "Respiration",
          isLabel: false,
          col: "col-6 border-0 ",
          type: "NewNumber",
          placeholder: "Respiration",
          name: "respiration",
          id: "respiration",
          Max: 3,
        },
        {
          label: "Temperature (°F)",
          isLabel: false,
          placeholder: "Temperature (°F)",
          type: "NewNumber",
          name: "temperature_f",
          id: "temperature_f",
          col: "col-6 border-0 ",
          decimalAllowed: true,
          Max: 6,
        },
        {
          label: "Oxygen Saturation (SpO2)",
          isLabel: false,
          placeholder: "SpO2",
          type: "NewNumber",
          name: "oxygen_saturation",
          id: "oxygen_saturation",
          col: "col-6 border-0 ",
          Max: 3,
        },
      ],
    },
    {
      label: "Vital Signs",
      labelStyle: sectionLabelStyle,
      headerClassName: sectionHeaderClass,
      headerStyle: sectionHeaderStyle,
      cardBodyClassName: "p-0 bg-white",
      fields: [
        { col: "col-12", label: "", type: "YesOrNoTable", useHeaderRadios: true, options: [{ label: "WITHIN NORMAL LIMITS", value: "Normal" }, { label: "ABNORMAL", value: "Abnormal" }], defaultValues: PHYSICAL_EXAM_SECTIONS.reduce((acc, itm) => { acc[itm.name] = "Normal"; return acc; }, {}), list: PHYSICAL_EXAM_SECTIONS, showNotes: true, failedValue: ["Abnormal"] },
        // { label: "Comments:", type: "TextArea", name: "clinician_comments", id: "clinician_comments", placeholder: "Comments", col: "col-12" },
        // { label: "Printed Name", type: "TextInput", col: "col-md-6 col-12", name: "clinician_printed_name", id: "clinician_printed_name", placeholder: "Printed Name" },
        // { label: "License Number & State", type: "TextInput", col: "col-md-6 col-12", name: "clinician_license_number_state", id: "clinician_license_number_state", placeholder: "License Number & State" },
        // { label: "Date", type: "Date", col: "col-md-6 col-12", name: "clinician_signature_date", id: "clinician_signature_date" },
        // { label: "Clinician Signature", type: "Signature", col: "col-md-6 col-12", name: "clinician_signature_data", id: "clinician_signature_data" },
        // { label: "Status", type: "SelectList", options: isSet(props.lookupsOptions?.chartResult, []), name: "status", id: "status" },
      ],
    },
  ];

  // Certification tab 
  const certificationTabData = [
    {
      label: "Medical Examiner Determination and Status",
      labelStyle: "fw-semibold",
      headerClassName: sectionHeaderClass,
      headerStyle: { backgroundColor: "#e8edf8", color: "#244186" },
      cardBodyClassName: sectionBodyClass,
      fields: [
        { label: "Medical Record", type: "TextInput", name: "medical_record", id: "medical_record", col: "col-md-6 col-12 border-0",placeholder: "Content", layout: "stacked" },
        { label: "Exam Date", type: "Date", name: "exam_date", id: "exam_date", col: "col-md-6 col-12 border-0", layout: "stacked" },
        { label: "Employment Status", type: "MultiSelectDropDown", ddKey: "employmentstatus", isToogle: true, options: isSet(props.employmenStatusOption, []), name: "employment_status", id: "employment_status", col: "col-md-6 col-12 border-0", layout: "stacked" },
        { label: "Provider", type: "SelectSearchList", isAsync: true, options: [], autoPopulateFields: { medical_examiners_certificate_expiration_date: "unavailable_until" }, loadCallBack: (val, callback) => fetchProviderList(val, props, callback), name: "provider", id: "provider", col: "col-md-6 col-12 border-0", layout: "stacked" },
        { label: "Notes", type: "TextArea", name: "notes", id: "certification_notes", col: "col-12 border-0", layout: "stacked", placeholder: "Add Comments" },
      ],
    },
    {
      label: "Review / Completion",
      labelStyle: "fw-semibold",
      headerClassName: sectionHeaderClass,
      headerStyle: { backgroundColor: "#e8edf8", color: "#244186" },
      cardBodyClassName: sectionBodyClass,
      fields: [
        {
          type: "ProviderSignOff",
          col: "col-12",
          fields: [
            {
              label: "Signature PIN",
              placeholder: "Signature PIN",
              name: "provider_pin",
              col: "col-12",
            },
            {
              label: "Signature",
              type: "NewSignature",
              name: "provider_signature",
              col: "col-12",
            },
            {
              label: "Date",
              type: "Date",
              name: "signed_date",
              col: "col-12",
            },
            {
              label: "Final Results",
              type: "SelectList",
              name: "status",
              col: "col-12",
              // Previous options:
              // options: isSet(props.lookupsOptions?.["chartResult"], []),
              // New standard options:
              options: optionsToKeyValuePairs(
                [
                  "Not Started",
                  "Pending",
                  "Normal",
                  "Abnormal",
                  "Negative",
                  "Positive",
                  "Dispensed",
                  "Administered",
                  "Failed",
                  "Passed",
                  "Non-Negative",
                  "Cancelled",
                  "Inconclusive",
                ],
                false
              ),
              statusBasedColor: true,
            },
            {
              label: "Activity Status",
              type: "SelectList",
              name: "activity_status",
              col: "col-12",
              // Previous options:
              // options: optionsToKeyValuePairs([
              //   "Not Started",
              //   "Ordered",
              //   "Completed",
              //   "Cancelled",
              // ]),
              // New standard options:
              options: optionsToKeyValuePairs(
                [
                  "Not Started",
                  "Pending",
                  "Pending Lab Confirmation",
                  "Pending MRO Confirmation",
                  "Ready for Review",
                  "Completed",
                  "Cancelled",
                ],
                false
              ),
              statusBasedColor: true,
            },
          ],
        },
      ],
    },
  ];

  const charts = [
    props.hasEditAccess
      ? {
          type: "Grid",
          col: "col-md-3",
          className: "non-dot-exam-scrollable",
          style: {
            position: "sticky",
            top: "0",
            zIndex: "1",
            height: "calc(100vh - 200px)",
            overflowY: "auto",
            overflowX: "hidden",
          },
          data: [
            { type: "PatientDetails", employeeDetails: props.employeeDetails, visitDetails: props.visitDetails },
            { type: "CompanyDetails", companyDetails: props.companyDetails },
            {
              type: "ResultSummary",
              results: [
                {
                  normalMsg: "",
                  abnormalMsg: "",
                  data: [
                    { label: "Pulse", keyVal: "pulse_rate_result" },
                    { label: "Blood Pressure", keyVal: "blood_pressure_result" },
                  ],
                },
              ],
            },
          ],
        }
      : null,
    {
      type: "Tab",
      col: `col-md-${props.hasEditAccess ? "9" : "12"}`,
      data: [
        { id: TAB_IDS.HISTORY, label: "History", data: historyTabData },
        { id: TAB_IDS.EXAM, label: "Exam", data: examTabData },
        { id: TAB_IDS.CERTIFICATION, label: "Certification", data: certificationTabData },
        { id: "Images", label: "Images", data: [] },
      ],
    },
  ];

  const headers = [];

  const defaultValue = {
    status: "Not Started",
    pulse_rate_result: "Not Started",
    blood_pressure_result: "Not Started",
    vission_result: "Not Started",
    urinalysis_result: "Not Started",
    medical_examiner_determination: "",
    past_medical_illness_none: 0,
    past_surgical_history_none: 0,
    medications_none: 0,
    allergies_none: 0,
    menstrual_comments_none: 0,
  };

  const buttons = { PDF: true };

  return {
    charts,
    defaultValue,
    buttons,
    headers,
    computationFunction: (formData, key) => ({
      // ...computationRules1(formData, key),
      ...computationRules(formData, key),
    }),
  };
};

const computationRules = (formData, key) => {
  const pulse = isSet(formData.pulse, "");
  const systolic = isSet(formData.sitting_systolic, "");
  const diastolic = isSet(formData.sitting_diastolic, "");
  let returnResult = {};

  const currentStatus = isSet(formData.status, "Not Started");
  const currentActivityStatus = isSet(formData.activity_status, "Not Started");

  // If user changes any field while both status and activity_status are "Not Started",
  // move to Pending Result / In Progress
  if (currentStatus === "Not Started" && currentActivityStatus === "Not Started" && key) {
    returnResult.status = "Pending";
    returnResult.activity_status = "Pending";
  }
  if(key === 'status' && formData.status) {
    const status = formData.status;
    // Check if provider has signed (provider_signature exists and is not empty)
    const providerSigned = formData.provider_signature && 
                          formData.provider_signature !== "" && 
                          formData.provider_signature !== null && 
                          formData.provider_signature !== undefined;
    
    switch(status) {
        case 'Not Started':
            returnResult.activity_status = 'Not Started';
            break;
        case 'Pending':
            returnResult.activity_status = 'Pending';
            break;
        case 'Normal':
            // If provider signed, set to Completed; otherwise Pending
            returnResult.activity_status = providerSigned ? 'Completed' : 'Pending';
            break;
        case 'Abnormal':
            returnResult.activity_status = providerSigned ? 'Completed' : 'Pending';
            break;
        case 'Negative':
            returnResult.activity_status = providerSigned ? 'Completed' : 'Pending';
            break;
        case 'Positive':
            returnResult.activity_status = providerSigned ? 'Completed' : 'Pending';
            break;
        case 'Dispensed':
            returnResult.activity_status = providerSigned ? 'Completed' : 'Pending';
            break;
        case 'Administered':
            returnResult.activity_status = providerSigned ? 'Completed' : 'Pending';
            break;
        case 'Failed':
            returnResult.activity_status = providerSigned ? 'Completed' : 'Pending';
            break;
        case 'Passed':
            returnResult.activity_status = providerSigned ? 'Completed' : 'Pending';
            break;
        case 'Non-Negative':
            returnResult.activity_status = 'Pending Lab Confirmation';
            break;
        case 'Cancelled':
            returnResult.activity_status = 'Cancelled';
            break;
        case 'Inconclusive':
            returnResult.activity_status = 'Pending Lab Confirmation';
            break;
        default:
            // No change if status doesn't match any case
            break;
    }
  }
  
  // Also check when provider_signature changes - if status is a completion status, update activity_status
  if(key === 'provider_signature' && formData.status) {
    const status = formData.status;
    const providerSigned = formData.provider_signature && 
                          formData.provider_signature !== "" && 
                          formData.provider_signature !== null && 
                          formData.provider_signature !== undefined;
    
    // Completion statuses that should become "Completed" when provider signs
    const completionStatuses = ['Normal', 'Abnormal', 'Negative', 'Positive', 'Dispensed', 'Administered', 'Failed', 'Passed'];
    
    if (completionStatuses.includes(status)) {
      returnResult.activity_status = providerSigned ? 'Completed' : 'Pending';
    }
  }
  
  let pulseRateResult = "Not Started";
  if (pulse == "") {
    pulseRateResult = "Not Started";
  } else if (pulse >= 60 && pulse <= 100) {
    pulseRateResult = "Normal";
  } else if (pulse !== "") {
    pulseRateResult = "Abnormal";
  }

  let bloodPressureResult = "Not Started";
  if (systolic === "" && diastolic === "") {
    bloodPressureResult = "Not Started";
  } else if (systolic === "" || diastolic === "") {
    bloodPressureResult = "Pending";
  } else if (
    systolic < 0 ||
    systolic > 139 ||
    diastolic < 0 ||
    diastolic > 89
  ) {
    bloodPressureResult = "Abnormal";
  } else {
    bloodPressureResult = "Normal";
  }

  const spgr = isSet(formData.sp_gr, "");
  const protein = isSet(formData.protein, "");
  const blood = isSet(formData.blood, "");
  const sugar = isSet(formData.sugar, "");

  let urinariesResult = "Not Started";

  const sgNormalRange = { min: 1.002, max: 1.03 };
  const sgWithinNormalRange =
    spgr >= sgNormalRange.min && spgr <= sgNormalRange.max;

  const abnormalLevels = protein > 0 || blood > 0 || sugar > 0;

  if (sgNormalRange === "" || protein === "" || blood === "" || sugar === 0) {
    urinariesResult = "Pending";
  } else if (sgWithinNormalRange && !abnormalLevels) {
    urinariesResult = "Normal";
  } else if (!sgWithinNormalRange || abnormalLevels) {
    urinariesResult = "Abnormal";
  } else {
    urinariesResult = "Pending";
  }

  let vissionResult = "Not Started";
  const wearing_corrective_lenses = isSet(
    formData["wearing_corrective_lenses"],
    null
  );
  const conditions =
    wearing_corrective_lenses === 0 ? "uncorrected" : "corrected";
  const horizontalRighteye = isSet(
    formData["horizontal_field_of_vision_right_eye"],
    ""
  );
  const horizontalLeftEye = isSet(
    formData["horizontal_field_of_vision_left_eye"],
    ""
  );
  const applicantRecoginize = isSet(
    formData["applicant_recognize_traffic_control_signals"],
    ""
  );
  const monocularvision = isSet(formData["monocular_vision"], "");

  if (wearing_corrective_lenses !== null) {
    const rightEye = isSet(formData["" + conditions + "_right_eye"], "");
    const bothEyes = isSet(formData["" + conditions + "_both_eyes"], "");
    const leftEye = isSet(formData["" + conditions + "_left_eye"], "");

    if (
      rightEye === "" ||
      bothEyes === "" ||
      leftEye === "" ||
      horizontalRighteye === "" ||
      horizontalLeftEye === ""
    ) {
      vissionResult = "Pending";
    } else if (
      rightEye > 40 ||
      bothEyes > 40 ||
      leftEye > 40 ||
      horizontalRighteye < 70 ||
      horizontalLeftEye < 70
    ) {
      vissionResult = "Failed";
    } else {
      vissionResult = "Passed";
    }
  } else {
    if (horizontalRighteye === "" || horizontalLeftEye === "") {
      vissionResult = "Pending";
    } else if (
      horizontalRighteye < 70 ||
      horizontalLeftEye < 70 ||
      applicantRecoginize === "No" ||
      monocularvision === "No"
    ) {
      vissionResult = "Failed";
    } else {
      vissionResult = "Passed";
    }
  }

  const earPrefixes = ["right_ear_", "left_ear_"];
  earPrefixes.forEach((earPrefix) => {
    const frequencies = [500, 1000, 2000, 3000, 4000, 6000, 8000];
    let total = 0;

    frequencies.forEach((frequency) => {
      const value = isSet(
        formData[`audiometric_test_results_${earPrefix}${frequency}_hz`],
        ""
      );
      total += Number(value);
    });
    const average = Math.ceil(total / frequencies.length);
    returnResult[`audiometric_test_results_${earPrefix}average`] = average;
  });

  const leftAverage =
    returnResult[`audiometric_test_results_right_ear_average`];
  const rightAverage =
    returnResult[`audiometric_test_results_left_ear_average`];
  const whisperTestRightEar = isSet(
    formData[`whisper_test_results_right_ear`],
    ""
  );
  const whisperTestLeftEar = isSet(
    formData[`whisper_test_results_left_ear`],
    ""
  );
  let hearingResult = "Not Started";
  if (
    Number(leftAverage) === 0 &&
    Number(rightAverage) === 0 &&
    whisperTestRightEar == "" &&
    whisperTestLeftEar === ""
  ) {
    hearingResult = "Not Started";
  } else if (
    (leftAverage !== "0.00" && leftAverage <= 40 && leftAverage !== 0) ||
    (rightAverage !== "0.00" && rightAverage <= 40 && rightAverage !== 0) ||
    whisperTestRightEar >= 5 ||
    whisperTestLeftEar >= 5
  ) {
    hearingResult = "Passed";
  } else if (
    (leftAverage === 0 || rightAverage === 0) &&
    (leftAverage > 40 || rightAverage > 40)
  ) {
    hearingResult = "Failed";
  } else if (
    leftAverage > 40 ||
    rightAverage > 40 ||
    (whisperTestRightEar < 5 && whisperTestLeftEar < 5)
  ) {
    hearingResult = "Failed";
  } else {
    hearingResult = "Pending";
  }
  returnResult = {
    ...returnResult,
    pulse_rate_result: pulseRateResult,
    blood_pressure_result: bloodPressureResult,
    vission_result: vissionResult,
    hearing_result: hearingResult,
    urinalysis_result: urinariesResult,
    certification_suggested_length: getCertificationLength(systolic, diastolic),
  };

  // Clear "Other Past Medical Conditions" text when that checkbox is unchecked
  if (key === "past_medical_illness") {
    const val = formData.past_medical_illness || "";
    const arr = typeof val === "string" ? val.split(",").map((s) => s.trim()) : Array.isArray(val) ? val : [];
    if (!arr.includes("other")) {
      returnResult.past_medical_illness_other_specify = "";
    }
  }

  return returnResult;
};

export const getCertificationLength = (systolic, diastolic) => {
  if (systolic === "" || diastolic === "") {
    return "";
  }
  if (systolic < 140 && diastolic < 90) {
    return "2 year DOT Certificate";
  } else if (
    systolic >= 140 &&
    systolic <= 159 &&
    diastolic >= 90 &&
    diastolic <= 99
  ) {
    return "1 year DOT Certificate";
  } else if (
    systolic >= 160 &&
    systolic <= 179 &&
    diastolic >= 100 &&
    diastolic <= 109
  ) {
    return "3 month DOT Certificate";
  } else if (systolic >= 180 || diastolic >= 110) {
    return "Disqualified";
  } else {
    return "1 year DOT Certificate";
  }
};
